import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, Edit3, Trash2 } from 'lucide-react';

export interface ActionCellProps {
  viewUrl?: string;
  editUrl?: string;
  onView?: (e: React.MouseEvent) => void;
  onEdit?: (e: React.MouseEvent) => void;
  onDelete?: (e: React.MouseEvent) => void;
  isBlocked?: boolean;
  blockedMessage?: string;
  className?: string;
}

/**
 * ActionCell Component
 * Renders consistent, accessible 32px icon action buttons (View, Edit, Delete)
 * with tooltips, hover effects, and keyboard focus states.
 */
export const ActionCell: React.FC<ActionCellProps> = ({
  viewUrl,
  editUrl,
  onView,
  onEdit,
  onDelete,
  isBlocked = false,
  className = '',
}) => {
  return (
    <div className={`flex items-center gap-1.5 h-full ${className}`}>
      {/* View Button */}
      {(viewUrl || onView) && (
        viewUrl && !isBlocked ? (
          <Link
            to={viewUrl}
            onClick={onView}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-[#1AA7DE] bg-[#1AA7DE]/10 hover:bg-[#1AA7DE] hover:text-white transition-all shadow-2xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1AA7DE]"
            title="View Details"
            aria-label="View Details"
          >
            <Eye className="w-4 h-4" />
          </Link>
        ) : (
          <button
            type="button"
            onClick={onView}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-[#1AA7DE] bg-[#1AA7DE]/10 hover:bg-[#1AA7DE] hover:text-white transition-all shadow-2xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1AA7DE]"
            title="View Details"
            aria-label="View Details"
          >
            <Eye className="w-4 h-4" />
          </button>
        )
      )}

      {/* Edit Button */}
      {(editUrl || onEdit) && (
        editUrl && !isBlocked ? (
          <Link
            to={editUrl}
            onClick={onEdit}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-600 bg-slate-100 hover:bg-slate-200 hover:text-slate-900 transition-all shadow-2xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
            title="Edit Item"
            aria-label="Edit Item"
          >
            <Edit3 className="w-4 h-4" />
          </Link>
        ) : (
          <button
            type="button"
            onClick={onEdit}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-600 bg-slate-100 hover:bg-slate-200 hover:text-slate-900 transition-all shadow-2xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
            title="Edit Item"
            aria-label="Edit Item"
          >
            <Edit3 className="w-4 h-4" />
          </button>
        )
      )}

      {/* Optional Delete Button */}
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-rose-600 bg-rose-50 hover:bg-rose-100 transition-all shadow-2xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
          title="Delete Item"
          aria-label="Delete Item"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default ActionCell;
